import RoundButton from '@deltek/specification-client/core/Buttons/RoundButton'
import { CurrentUserInfo } from '@deltek/specification-client/core/Models/currentUserInfo'
import {
    ManufacturerProductCatalogDetail,
    ManufacturerProductSpecDocDetail,
} from '@deltek/specification-client/core/Models/product'
import {
    ListingDetailCatalogInput,
    ListingDetailDocumentInput,
} from '@deltek/specification-client/core/Models/productListingDetail'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableCell from '@material-ui/core/TableCell'
import TableRow from '@material-ui/core/TableRow'
import Typography from '@material-ui/core/Typography'
import { Theme, makeStyles } from '@material-ui/core/styles'
import { isUserDeltekAdmin } from 'bpm/pages/ProductsPage/productsUtils'
import clsx from 'clsx'
import React from 'react'
import { EditDocumentRow } from './EditDocumentRow'
const useStyles = makeStyles((theme: Theme) => ({
    container: {
        maxWidth: 504,
    },
    fullWidth: {
        width: '100%',
    },
    title: {
        color: '#04325B',
        fontWeight: 500,
        borderBottom: '6px solid #04325B',
        textTransform: 'uppercase',
    },
    itemContainer: {
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(1),
    },
    tableWhiteText: {
        color: '#FFF',
        fontWeight: 500,
    },
    buttonText: {
        ...theme.typography.h6,
        color: '#04325B',
    },
    buttonTextSelected: {
        color: '#FFF',
    },
    buttonContainer: {
        borderBottom: '1px solid #FFFFFF',
        '& button.MuiButton-contained': {
            background: '#FFFFFF',
            borderRadius: 50,
        },
        paddingRight: `0 !important`,
        paddingTop: `${theme.spacing(1.5)}px !important`,
    },
    buttonContainerSelected: {
        '& button.MuiButton-contained': {
            background: '#12ab8f !important',
            '&::before': {
                content: "'\\2713'",
                display: 'inline-block',
                color: '#FFFFFF',
                marginRight: '0.2em',
            },
        },
    },
}))

interface EditDocumentsProps {
    currentUserInfo?: CurrentUserInfo
    catalog?: Partial<ManufacturerProductCatalogDetail>
    longDocType?: Partial<ManufacturerProductSpecDocDetail>
    shortDocType?: Partial<ManufacturerProductSpecDocDetail>
    handleDocumentsChange: (
        longDocType: Partial<ListingDetailDocumentInput>,
        shortDocType: Partial<ListingDetailDocumentInput>,
        catalog: Partial<ListingDetailCatalogInput>
    ) => void
    handleFilestackLoading?: (filestackLoading: boolean | null) => void
    handleTransferringFiles?: (value: boolean) => void
    showProductSpecification?: boolean
    setAutoGeneratePSD?: React.Dispatch<React.SetStateAction<boolean>>
    isListingTypeValidForPSDBtn?: boolean
}

export const EditDocuments: React.FC<EditDocumentsProps> = ({
    currentUserInfo,
    catalog,
    longDocType,
    shortDocType,
    handleDocumentsChange,
    handleFilestackLoading,
    handleTransferringFiles,
    showProductSpecification,
    setAutoGeneratePSD,
    isListingTypeValidForPSDBtn,
}) => {
    const classes = useStyles()

    const [localShortDoc, setLocalShortDoc] = React.useState<
        Partial<ListingDetailDocumentInput>
    >(
        shortDocType
            ? {
                  id: shortDocType.id,
                  expirationDate: shortDocType.expirationDate,
                  docVariation: shortDocType.docVariation,
                  title: shortDocType.title,
                  fileName: shortDocType.path,
                  docType: 'ShortForm',
              }
            : {
                  title: '',
                  docType: 'ShortForm',
              }
    )
    const [localLongDoc, setLocalLongDoc] = React.useState<
        Partial<ListingDetailDocumentInput>
    >(
        longDocType
            ? {
                  id: longDocType.id,
                  expirationDate: longDocType.expirationDate,
                  docVariation: longDocType.docVariation,
                  title: longDocType.title,
                  fileName: longDocType.path,
                  docType: 'FullLength',
              }
            : { title: '', docType: 'FullLength' }
    )

    const catalogPathArr = catalog?.path?.split('/')
    const [localCatalog, setLocalCatalog] = React.useState<
        Partial<ListingDetailCatalogInput>
    >(
        catalog
            ? {
                  // support for old db records
                  title: catalog?.title
                      ? catalog?.title !== ''
                          ? catalog?.title
                          : catalogPathArr?.[catalogPathArr?.length - 1]
                      : '',
              }
            : { title: '' }
    )

    const [transferringCatalog, setTransferringCatalog] = React.useState<
        boolean
    >(false)
    const [transferringShortDoc, setTransferringShortDoc] = React.useState<
        boolean
    >(false)
    const [transferringFullDoc, setTransferringFullDoc] = React.useState<
        boolean
    >(false)

    const handleShortDocChange = (
        value: Partial<ListingDetailDocumentInput>
    ) => {
        handleDocumentsChange(localLongDoc, value, localCatalog)
        setLocalShortDoc(value)
    }

    const handleLongDocChange = (
        value: Partial<ListingDetailDocumentInput>
    ) => {
        handleDocumentsChange(value, localShortDoc, localCatalog)
        setLocalLongDoc(value)
    }

    const handleCatalogChange = (value: Partial<ListingDetailCatalogInput>) => {
        handleDocumentsChange(localLongDoc, localShortDoc, value)
        setLocalCatalog(value)
    }

    const handleTransferringCatalogFile = (
        value: boolean,
        uploadDone?: boolean
    ) => {
        if (value) {
            setTransferringCatalog(true)
        }
        if (uploadDone) {
            setTransferringCatalog(false)
        }
    }

    const handleTransferringShortFile = (
        value: boolean,
        uploadDone?: boolean
    ) => {
        if (value) {
            setTransferringShortDoc(true)
        }
        if (uploadDone) {
            setTransferringShortDoc(false)
        }
    }

    const handleTransferringLongFile = (
        value: boolean,
        uploadDone?: boolean
    ) => {
        if (value) {
            setTransferringFullDoc(true)
        }
        if (uploadDone) {
            setTransferringFullDoc(false)
        }
    }

    const isDeltekAdmin = isUserDeltekAdmin({ currentUserInfo })
    const showProductspecificationButton =
        isDeltekAdmin && isListingTypeValidForPSDBtn

    React.useEffect(() => {
        handleTransferringFiles?.(
            transferringCatalog || transferringFullDoc || transferringShortDoc
        )
    }, [
        handleTransferringFiles,
        transferringCatalog,
        transferringFullDoc,
        transferringShortDoc,
    ])

    return (
        <GridContainer data-testid="dvDocuments" className={classes.container}>
            <GridItem className={classes.fullWidth}>
                <Typography variant="h6" className={classes.title}>
                    Download Documents
                </Typography>
            </GridItem>
            <GridItem
                className={clsx(classes.itemContainer, classes.fullWidth)}
            >
                <Table className={clsx(classes.container)} size="small">
                    <TableBody className={classes.fullWidth}>
                        {isDeltekAdmin && (
                            <>
                                <EditDocumentRow
                                    key="longform"
                                    text="Full Length Document"
                                    value={localLongDoc}
                                    handleChange={handleLongDocChange}
                                    handleFilestackLoading={
                                        handleFilestackLoading
                                    }
                                    transferringFiles={transferringFullDoc}
                                    handleTransferringFiles={
                                        handleTransferringLongFile
                                    }
                                    disableButtons={
                                        transferringFullDoc ||
                                        transferringShortDoc ||
                                        transferringCatalog
                                    }
                                />
                                <EditDocumentRow
                                    key="shortform"
                                    text="Short Form Document"
                                    value={localShortDoc}
                                    handleChange={handleShortDocChange}
                                    handleFilestackLoading={
                                        handleFilestackLoading
                                    }
                                    transferringFiles={transferringShortDoc}
                                    handleTransferringFiles={
                                        handleTransferringShortFile
                                    }
                                    disableButtons={
                                        transferringFullDoc ||
                                        transferringShortDoc ||
                                        transferringCatalog
                                    }
                                />
                            </>
                        )}
                        {!!showProductspecificationButton && (
                            <TableRow className="documentType">
                                <TableCell
                                    padding="none"
                                    className={clsx(
                                        classes.buttonContainer,
                                        (showProductSpecification &&
                                            classes.buttonContainerSelected) ||
                                            ''
                                    )}
                                >
                                    <RoundButton
                                        hasShadow={false}
                                        borderRadius={50}
                                        color={
                                            showProductSpecification
                                                ? '#fff'
                                                : '#04325B'
                                        }
                                        borderColor="#04325B"
                                        data-testid="generatePSD"
                                        onClick={() =>
                                            setAutoGeneratePSD?.(
                                                !showProductSpecification
                                            )
                                        }
                                    >
                                        <Typography
                                            className={clsx(
                                                classes.buttonText,
                                                (showProductSpecification &&
                                                    classes.buttonTextSelected) ||
                                                    ''
                                            )}
                                            component="span"
                                        >
                                            Generate Product Specification
                                        </Typography>
                                    </RoundButton>
                                </TableCell>
                            </TableRow>
                        )}
                        <EditDocumentRow
                            last
                            key="catalog"
                            text="Catalog"
                            value={localCatalog}
                            handleChange={handleCatalogChange}
                            handleFilestackLoading={handleFilestackLoading}
                            transferringFiles={transferringCatalog}
                            handleTransferringFiles={
                                handleTransferringCatalogFile
                            }
                            disableButtons={
                                transferringFullDoc ||
                                transferringShortDoc ||
                                transferringCatalog
                            }
                        />
                    </TableBody>
                </Table>
            </GridItem>
        </GridContainer>
    )
}
